import { Button } from "@patient-watch/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@patient-watch/ui/dialog";
import { Input } from "@patient-watch/ui/input";
import { Label } from "@patient-watch/ui/label";
import { Slider } from "@patient-watch/ui/slider";
import { AlertTriangle, ChevronDown, ChevronUp, Edit, Mail, Phone } from "lucide-react";
import React, { useState } from "react";
import { formatMeasurementResult } from "../utils/formatMeasurementResult";
import { PlanCard } from "./PlanCard";
import { RichLabel } from "./RichLabel";

// Safety Risk Warning component for HIGH RISK PHQ-9 responses
function SafetyRiskWarning() {
    return (
        <div className="bg-red-50 dark:bg-red-950 p-4 border-2 border-red-500 rounded-lg">
            <div className="flex items-start gap-3">
                <AlertTriangle className="flex-shrink-0 mt-0.5 w-6 h-6 text-red-600 dark:text-red-400" />
                <div className="space-y-3">
                    <div>
                        <h4 className="font-bold text-red-800 dark:text-red-200 text-lg">
                            Safety Risk Assessment: HIGH RISK
                        </h4>
                        <p className="mt-1 text-red-700 dark:text-red-300 text-sm">
                            This patient has indicated frequent thoughts of self-harm or suicide. Immediate clinical
                            follow-up is recommended.
                        </p>
                    </div>
                    <div className="bg-white dark:bg-slate-900 p-3 rounded-md">
                        <h5 className="flex items-center gap-2 mb-2 font-semibold text-red-800 dark:text-red-200 text-sm">
                            <Phone size={16} />
                            Crisis Resources
                        </h5>
                        <ul className="space-y-1 text-red-700 dark:text-red-300 text-sm">
                            <li>
                                <strong>Samaritans:</strong> 116 123 (free, 24/7)
                            </li>
                            <li>
                                <strong>NHS Crisis Line:</strong> 111, then select mental health
                            </li>
                            <li>
                                <strong>SHOUT:</strong> Text "SHOUT" to 85258
                            </li>
                            <li>
                                <strong>Emergency:</strong> 999 if in immediate danger
                            </li>
                        </ul>
                    </div>
                    <p className="text-red-600 dark:text-red-300 text-xs">
                        Please ensure this patient receives appropriate clinical support as soon as possible.
                    </p>
                </div>
            </div>
        </div>
    );
}

interface EntryValue {
    entry_value_id: string;
    input_index: number;
    input_label: string;
    value?: string | number | boolean | string[] | Date | null;
    text_value?: string | null;
    number_value?: number | null;
    json_value?: Record<string, unknown> | null;
    input_key?: string;
    page_index?: number | null;
    page_label?: string;
    chart_order?: number | null;
    is_primary_score?: boolean;
    min_value?: number | null;
    max_value?: number | null;
    /** `entry_values_view.inputs` carries the input's min/max when the flat fields are absent. */
    inputs?: { min_value?: number | null; max_value?: number | null } | null;
    text_array?: string[] | null;
    /** Options linked to the answer (`entry_values_view.options`), in answer order. */
    options?: { value?: unknown; label?: string | null }[] | null;
}

function getSliderRange(ev: EntryValue): { min: number; max: number } {
    return {
        min: ev.min_value ?? ev.inputs?.min_value ?? 0,
        max: ev.max_value ?? ev.inputs?.max_value ?? 100,
    };
}

/** Only the 0-100 health VAS (EQ-5D) gets the vertical thermometer; other sliders show "value / max". */
function isHealthVasSlider(ev: EntryValue): boolean {
    return ev.input_key === "slider" && ev.number_value != null && getSliderRange(ev).max === 100;
}

const CHARTABLE_LEGACY_INPUT_KEYS = ["number", "pain-slider", "slider", "single-choice"] as const;

function isCalculatedScoreEntryValue(ev: EntryValue, allEntryValues: EntryValue[]): boolean {
    if (ev.page_index != null) return false;
    if (ev.input_key === "warning") return true;

    const hasChartOrderMeta = allEntryValues.some((s) => s.chart_order != null);
    if (hasChartOrderMeta) {
        return ev.chart_order != null || ev.is_primary_score === true;
    }

    return CHARTABLE_LEGACY_INPUT_KEYS.includes(ev.input_key as (typeof CHARTABLE_LEGACY_INPUT_KEYS)[number]);
}

function sortCalculatedScores(entryValues: EntryValue[]): EntryValue[] {
    return [...entryValues].sort((a, b) => {
        const keyA = a.is_primary_score && a.chart_order == null ? 0 : (a.chart_order ?? 9_999);
        const keyB = b.is_primary_score && b.chart_order == null ? 0 : (b.chart_order ?? 9_999);
        if (keyA !== keyB) return keyA - keyB;
        return a.input_label.localeCompare(b.input_label);
    });
}

// Visual slider display component for results using the actual Slider component
function SliderResultDisplay({
    value,
    minValue = 0,
    maxValue = 100,
    minLabel,
    maxLabel,
}: {
    value: number;
    minValue?: number;
    maxValue?: number;
    minLabel?: string;
    maxLabel?: string;
}) {
    return (
        <div className="flex flex-row justify-start sm:justify-center items-stretch gap-4 sm:gap-6 py-4 max-w-full sm:overflow-visible overflow-x-auto">
            {/* Current value display on the left */}
            <div className="flex items-center">
                <div className="flex justify-center items-center bg-indigo-100 dark:bg-slate-800 border-4 border-indigo-500 dark:border-indigo-400 rounded-lg w-20 h-20">
                    <span className="font-bold text-indigo-900 dark:text-slate-100 text-3xl">{value}</span>
                </div>
            </div>

            {/* Slider with labels on the right */}
            <div className="flex flex-col items-center gap-2">
                {/* Max label (top/best) */}
                {maxLabel && (
                    <div className="text-center">
                        <span className="font-semibold text-slate-700 dark:text-slate-200 text-lg">{maxValue}</span>
                        <p className="max-w-[150px] text-muted-foreground text-xs">{maxLabel}</p>
                    </div>
                )}

                {/* Vertical slider - disabled/read-only */}
                <div className="flex justify-center py-2 h-[200px]">
                    <Slider
                        value={[value]}
                        min={minValue}
                        max={maxValue}
                        orientation="vertical"
                        disabled
                        className="h-full"
                        thumbClassName="border-4 border-indigo-500 w-8 h-8"
                    />
                </div>

                {/* Min label (bottom/worst) */}
                {minLabel && (
                    <div className="text-center">
                        <span className="font-semibold text-slate-700 dark:text-slate-200 text-lg">{minValue}</span>
                        <p className="max-w-[150px] text-muted-foreground text-xs">{minLabel}</p>
                    </div>
                )}
            </div>
        </div>
    );
}

interface FormResultsCardProps {
    formName?: string;
    entryValues: EntryValue[];
    isLoading?: boolean;
    onEdit?: () => void;
    onEmailSend?: (clinicianEmail: string, patientEmail: string) => Promise<void>;
}

const BRANCHING_SCALE_INPUT_KEYS = new Set(["visa-a-8", "visa-g-8", "visa-h-8", "visa-p-8"]);
const RATINGS_LIST_INPUT_KEYS = new Set(["ratings-list", "ms-adl-ratings-list", "fixed-ratings-list"]);

interface ActivityRating {
    activity: string;
    score: number;
}

/** Activity ratings stored as JSON, e.g. `{"Sport": 7}`. */
function parseActivityRatings(raw: unknown): ActivityRating[] | null {
    let parsed: unknown = raw;
    if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return null;
        try {
            parsed = JSON.parse(trimmed);
        } catch {
            return null;
        }
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;

    const ratings: ActivityRating[] = [];
    for (const [activity, score] of Object.entries(parsed as Record<string, unknown>)) {
        const numeric = typeof score === "number" ? score : typeof score === "string" ? Number(score) : Number.NaN;
        if (!activity.trim() || !Number.isFinite(numeric)) return null;
        ratings.push({ activity, score: numeric });
    }
    return ratings.length > 0 ? ratings : null;
}

function activityRatingsFromEntry(ev: EntryValue): ActivityRating[] | null {
    const fromValue = parseActivityRatings(ev.value);
    if (fromValue) return fromValue;
    return parseActivityRatings(ev.json_value);
}

/** Read-only match of the activity slider on the Patient-Specific Functional Scale. */
function RatingsListResultDisplay({ ratings }: { ratings: ActivityRating[] }) {
    return (
        <div className="space-y-3 pt-2">
            {ratings.map(({ activity, score }) => (
                <div
                    key={activity}
                    className="space-y-3 bg-white dark:bg-slate-800 p-4 border border-slate-200 dark:border-slate-700 rounded-lg"
                    data-testid="ratings-list-result"
                >
                    <p className="font-medium text-slate-900 dark:text-slate-100 text-base">{activity}</p>
                    <div className="flex justify-center items-baseline gap-1">
                        <span className="font-bold tabular-nums text-slate-900 dark:text-slate-100 text-2xl">
                            {score}
                        </span>
                        <span className="text-slate-500 dark:text-slate-400 text-lg">/10</span>
                    </div>
                    <Slider
                        value={[Math.min(10, Math.max(0, score))]}
                        min={0}
                        max={10}
                        step={1}
                        disabled
                        thumbClassName="disabled:opacity-100"
                        aria-label={`${activity} difficulty`}
                    />
                    <div className="flex justify-between gap-3 text-slate-600 dark:text-slate-300 text-xs">
                        <span>No difficulty</span>
                        <span className="text-right">Unable to do</span>
                    </div>
                </div>
            ))}
        </div>
    );
}

function parseBranchingScaleText(text: string | null | undefined): {
    branchLabel?: string;
    optionLabel?: string;
    branchText?: string;
} {
    if (!text?.trim()) return {};

    const pipeParts = text
        .split("|")
        .map((part) => part.trim())
        .filter(Boolean);
    if (pipeParts.length >= 2) {
        return {
            branchLabel: pipeParts[0],
            optionLabel: pipeParts[1],
            branchText: pipeParts[2],
        };
    }

    const [branchLabel, optionLabel] = text.split(/\s*[·|]\s*/, 2).map((part) => part.trim());
    return { branchLabel, optionLabel };
}

function formatBranchingScaleDisplay(ev: EntryValue): React.ReactNode {
    const score =
        typeof ev.number_value === "number"
            ? ev.number_value
            : typeof ev.value === "number"
              ? ev.value
              : ev.value != null && ev.value !== ""
                ? Number(ev.value)
                : null;

    if (score == null || Number.isNaN(score)) {
        return ev.text_value ?? "";
    }

    const { branchLabel, optionLabel, branchText } = parseBranchingScaleText(ev.text_value);

    return (
        <div className="space-y-3 pt-2">
            <div className="flex items-start gap-3">
                <span className="inline-flex justify-center items-center bg-indigo-600 rounded-full w-10 h-10 font-semibold text-white text-lg shrink-0">
                    {branchLabel || "?"}
                </span>
                <div className="pt-1.5 min-w-0">
                    <p className="font-medium text-slate-900 dark:text-slate-100 text-base leading-snug">
                        {branchText || (branchLabel ? `Option ${branchLabel}` : "Selected option")}
                    </p>
                </div>
            </div>

            <div className="flex justify-between items-center gap-4 bg-white dark:bg-slate-800 ml-[52px] px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg">
                <p className="min-w-0 font-medium text-slate-800 dark:text-slate-100 text-base leading-snug">
                    {optionLabel || "Duration not recorded"}
                </p>
                <div className="text-right shrink-0">
                    <p className="font-semibold tabular-nums text-slate-900 dark:text-slate-100 text-xl leading-none">
                        {score}
                    </p>
                    <p className="mt-0.5 text-slate-500 dark:text-slate-400 text-xs">points</p>
                </div>
            </div>
        </div>
    );
}

/**
 * A choice answer as its option labels. Codes and own options ("add your own option", stored as the option id)
 * become the linked option's label. Null when the answer has no linked options.
 */
function linkedChoiceLabels(ev: EntryValue): string | null {
    const options = ev.options ?? [];
    if (options.length === 0) return null;
    const stored = ev.text_array?.length
        ? ev.text_array
        : Array.isArray(ev.value)
          ? ev.value.map(String)
          : [ev.text_value ?? (ev.number_value != null ? String(ev.number_value) : String(ev.value ?? ""))];
    return stored
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item) => options.find((option) => String(option.value ?? "") === item)?.label?.trim() || item)
        .join(", ");
}

// Helper function to format entry value for display
const formatEntryValueDisplay = (ev: EntryValue): string | React.ReactNode => {
    // For checkbox, capitalise true/false
    if (ev.input_key === "checkbox") {
        const isChecked =
            ev.value === 1 ||
            ev.value === "true" ||
            ev.text_value === "true" ||
            (typeof ev.value === "string" && ev.value.toLowerCase() === "true");
        return isChecked ? "True" : "False";
    }

    // For scale-rating, show both number and text label
    if (ev.input_key === "scale-rating" && ev.number_value !== null && ev.number_value !== undefined) {
        if (ev.text_value) {
            return `${ev.number_value} - ${ev.text_value}`;
        }
        return String(ev.number_value);
    }

    if (BRANCHING_SCALE_INPUT_KEYS.has(ev.input_key ?? "")) {
        return formatBranchingScaleDisplay(ev);
    }

    if (ev.input_key === "slider" && ev.number_value != null) {
        return `${ev.number_value} / ${getSliderRange(ev).max}`;
    }

    if (ev.input_key === "measurement") {
        return formatMeasurementResult(ev.number_value, ev.text_value);
    }

    if (RATINGS_LIST_INPUT_KEYS.has(ev.input_key ?? "")) {
        const ratings = activityRatingsFromEntry(ev);
        if (ratings) return <RatingsListResultDisplay ratings={ratings} />;
    }

    const choiceLabels = linkedChoiceLabels(ev);
    if (choiceLabels) return choiceLabels;

    const value = ev.value;

    // Handle null/undefined
    if (value === null || value === undefined) {
        return "";
    }

    // Handle arrays (multiple choice)
    if (Array.isArray(value)) {
        return value.join(", ");
    }

    // JSON activity ratings, e.g. {"Sport": 7}. Brace-stripping would leave a stray quote.
    if (typeof value === "string" && value.startsWith("{") && value.endsWith("}")) {
        const ratings = parseActivityRatings(value);
        if (ratings) return <RatingsListResultDisplay ratings={ratings} />;

        try {
            JSON.parse(value);
        } catch {
            const cleaned = value.slice(1, -1);
            if (!cleaned) return "";
            return cleaned
                .split(",")
                .map((item) => item.trim().replace(/^"|"$/g, ""))
                .filter(Boolean)
                .join(", ");
        }
    }

    // For other types, just show the value
    return String(value);
};

export const FormResultsCard = ({
    formName,
    entryValues,
    isLoading = false,
    onEdit,
    onEmailSend,
}: FormResultsCardProps) => {
    const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
    const [clinicianEmail, setClinicianEmail] = useState("");
    const [patientEmail, setPatientEmail] = useState("");
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [emailSent, setEmailSent] = useState(false);

    const handleSendEmail = async () => {
        if (!clinicianEmail && !patientEmail) {
            return;
        }

        setIsSendingEmail(true);

        try {
            await onEmailSend?.(clinicianEmail, patientEmail);
            setEmailSent(true);
            setTimeout(() => {
                setIsEmailDialogOpen(false);
                setEmailSent(false);
                setClinicianEmail("");
                setPatientEmail("");
            }, 2000);
        } catch (error) {
            console.error("Error sending email:", error);
        } finally {
            setIsSendingEmail(false);
        }
    };

    if (isLoading) {
        return (
            <PlanCard
                badge="free"
                title="Loading Results..."
                description="Please wait while we process your submission"
                isLoading={true}
            />
        );
    }

    const actionButtons = (
        <div className="flex md:flex-row flex-col gap-3 h-fit">
            {onEmailSend && (
                <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
                    <DialogTrigger asChild>
                        <Button variant="default" size="lg" className="">
                            <Mail size={20} />
                            Send Results
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Email Results</DialogTitle>
                            <DialogDescription>
                                Enter email addresses to receive the form results. Both fields are optional.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="clinician-email">Clinician Email (Optional)</Label>
                                <Input
                                    id="clinician-email"
                                    type="email"
                                    placeholder="clinician@example.com"
                                    value={clinicianEmail}
                                    onChange={(e) => setClinicianEmail(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="patient-email">Patient Email (Optional)</Label>
                                <Input
                                    id="patient-email"
                                    type="email"
                                    placeholder="patient@example.com"
                                    value={patientEmail}
                                    onChange={(e) => setPatientEmail(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                size="lg"
                                onClick={() => setIsEmailDialogOpen(false)}
                                className=""
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSendEmail}
                                disabled={isSendingEmail || (!clinicianEmail && !patientEmail)}
                                className=""
                            >
                                {isSendingEmail ? "Sending..." : emailSent ? "Sent!" : "Send Email"}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
            {onEdit && (
                <Button variant="secondary" size="lg" className="" onClick={onEdit} data-testid="edit-answers-button">
                    <Edit size={20} />
                    Edit Answers
                </Button>
            )}
        </div>
    );

    return (
        <PlanCard
            badge="free"
            title={`${formName?.toUpperCase() || "Form"} Results`}
            description="Your submission summary"
            actions={onEmailSend || onEdit ? actionButtons : undefined}
        >
            <FormResultsBody entryValues={entryValues} />
        </PlanCard>
    );
};

/** Scores and answers, without the plan card chrome. */
export function FormResultsBody({ entryValues }: { entryValues: EntryValue[] }) {
    const [isExpanded, setIsExpanded] = useState(false);

    const calculatedScores = sortCalculatedScores(
        entryValues.filter((ev) => isCalculatedScoreEntryValue(ev, entryValues))
    );
    const otherValues = entryValues.filter((ev) => ev.page_index != null);
    const pageValues = otherValues.reduce(
        (acc, ev) => {
            const pageIndex = ev.page_index as number;
            if (!acc[pageIndex]) {
                acc[pageIndex] = [];
            }
            acc[pageIndex].push(ev);
            return acc;
        },
        {} as Record<number, EntryValue[]>
    );

    const hasCalculatedScore = calculatedScores.some(
        (ev) => ev.input_key !== "warning" || ev.text_value === "HIGH RISK"
    );
    // A calculated score has no page index. Without one, the answers are the
    // whole summary, so show them in the open list instead of the accordion.
    const shouldAlwaysShow = !hasCalculatedScore || otherValues.length < 3;

    return (
        <div className="space-y-4 min-w-0" data-testid="form-results-card">
            {/* Calculated Scores and Warnings */}
            {calculatedScores.map((ev) => {
                // Create a slug from the label for data-testid
                const scoreSlug = ev.input_label.toLowerCase().replace(/\s+/g, "-");

                // Handle warning input type (e.g., PHQ-9 Safety Risk Assessment)
                if (ev.input_key === "warning" && ev.text_value === "HIGH RISK") {
                    return <SafetyRiskWarning key={ev.entry_value_id} />;
                }

                // Skip other warning types that don't have HIGH RISK
                if (ev.input_key === "warning") {
                    return null;
                }

                return (
                    <div
                        key={ev.entry_value_id}
                        className="bg-gradient-to-r from-indigo-50 dark:from-slate-800 to-purple-50 dark:to-slate-800 p-4 border border-indigo-100 dark:border-slate-700 rounded-lg"
                        data-testid={`calculated-score-${scoreSlug}`}
                    >
                        <div className="space-y-1 text-center">
                            <h3
                                className="font-medium text-indigo-700 dark:text-slate-300 text-xs uppercase tracking-wide"
                                data-testid={`score-label-${scoreSlug}`}
                            >
                                <RichLabel>{ev.input_label}</RichLabel>
                            </h3>
                            <div
                                className="font-bold text-indigo-900 dark:text-slate-100 text-3xl"
                                data-testid={`score-value-${scoreSlug}`}
                            >
                                {formatEntryValueDisplay(ev)}
                            </div>
                        </div>
                    </div>
                );
            })}

            {/* Page Results - Expandable */}
            {Object.keys(pageValues).length > 0 && (
                <>
                    {(isExpanded || shouldAlwaysShow) && (
                        <div className="space-y-4">
                            {Object.entries(pageValues).map(([pageIndex, values]) => (
                                <div
                                    key={pageIndex}
                                    className="space-y-2 pt-4 border-slate-200 dark:border-slate-700 border-t"
                                >
                                    <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-lg">
                                        {values[0]?.page_label || `Page ${pageIndex}`}
                                    </h4>
                                    <div className="gap-3 grid">
                                        {values.map((ev) => {
                                            // Check if this is a slider input with a number value (like EQ-5D VAS)
                                            const isSlider = isHealthVasSlider(ev);
                                            const sliderRange = getSliderRange(ev);

                                            return (
                                                <div
                                                    key={ev.entry_value_id}
                                                    className="bg-slate-50 dark:bg-slate-800 p-4 border border-slate-100 dark:border-slate-700 rounded-lg"
                                                >
                                                    <div className="space-y-1">
                                                        <h5 className="font-normal text-slate-600 dark:text-slate-300 text-sm whitespace-pre-line">
                                                            <RichLabel>{ev.input_label}</RichLabel>
                                                        </h5>
                                                        {isSlider ? (
                                                            <SliderResultDisplay
                                                                value={ev.number_value as number}
                                                                minValue={sliderRange.min}
                                                                maxValue={sliderRange.max}
                                                                minLabel="The worst health you can imagine"
                                                                maxLabel="The best health you can imagine"
                                                            />
                                                        ) : (
                                                            <div className="font-medium text-slate-900 dark:text-slate-100 text-lg">
                                                                {formatEntryValueDisplay(ev)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {!shouldAlwaysShow && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
                            onClick={() => setIsExpanded(!isExpanded)}
                        >
                            {isExpanded ? (
                                <>
                                    <ChevronUp className="mr-1 w-4 h-4" />
                                    Hide Questions
                                </>
                            ) : (
                                <>
                                    <ChevronDown className="mr-1 w-4 h-4" />
                                    Show Questions ({otherValues.length})
                                </>
                            )}
                        </Button>
                    )}
                </>
            )}
        </div>
    );
}
